import { useEffect, useMemo, useState } from 'react';
import { InvoicePill } from '../components/Pill';
import { DateInput } from '../components/DateInput';
import { api, apiBlob } from '../lib/api';
import { fmt, fmtDate, todayIso } from '../lib/format';
import type { InvoiceStatus } from '@otter/shared';

type Props = {
  id: string;
  isAdmin: boolean;
  onClose: () => void;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type Line = { id?: string; position: number; description: string; quantity: number; unitPrice: number };

type Invoice = {
  id: string;
  formattedNumber: string | null;
  status: InvoiceStatus;
  clientId: string;
  ownerId: string;
  concept: string;
  issueDate: string | null;
  sendDate: string | null;
  payDate: string | null;
  dueDate: string | null;
  baseAmount: string;
  irpfPct: string;
  ivaPct: string;
  irpfAmount: string;
  ivaAmount: string;
  totalAmount: string;
  notes: string | null;
  isPrivate: boolean;
};

type Client = { id: string; name: string; nif: string; address: string | null; city: string | null; postalCode: string | null; country: string | null };
type Issuer = { legalName: string; nif: string; address: string | null; city: string | null; postalCode: string | null; country: string | null; iban: string | null };

const STEPS: InvoiceStatus[] = ['borrador', 'emitida', 'enviada', 'cobrada'];

export function EditorFactura({ id, isAdmin, onClose, onToast, onError }: Props) {
  const isNew = id === 'new';
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [issuer, setIssuer] = useState<Issuer | null>(null);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [lines, setLines] = useState<Line[]>([{ position: 0, description: '', quantity: 1, unitPrice: 0 }]);
  const [clientId, setClientId] = useState<string>('');
  const [concept, setConcept] = useState('');
  const [dueDate, setDueDate] = useState<string>('');
  const [irpfPct, setIrpfPct] = useState<number>(15);
  const [ivaPct, setIvaPct] = useState<number>(21);
  const [notes, setNotes] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const editable = !invoice || invoice.status === 'borrador';

  useEffect(() => {
    if (!pdfUrl) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closePdfPreview();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pdfUrl]);

  useEffect(() => {
    void (async () => {
      try {
        const isRes = await api<{ issuer: Issuer | null }>('/admin/issuer');
        setIssuer(isRes.issuer);
        const cRes = await api<{ items: Client[] }>('/admin/clients');
        setAllClients(cRes.items);
        if (!isNew) {
          const r = await api<{ invoice: Invoice; lines: Line[]; client: Client }>(
            `/admin/invoices-issued/${id}`,
          );
          setInvoice(r.invoice);
          setClient(r.client);
          setClientId(r.client.id);
          setConcept(r.invoice.concept);
          setDueDate(r.invoice.dueDate ?? '');
          setIrpfPct(Number.parseFloat(r.invoice.irpfPct));
          setIvaPct(Number.parseFloat(r.invoice.ivaPct));
          setNotes(r.invoice.notes ?? '');
          setIsPrivate(r.invoice.isPrivate);
          setLines(
            r.lines.length > 0
              ? r.lines.map((l) => ({
                  ...l,
                  quantity: Number.parseFloat(l.quantity as unknown as string),
                  unitPrice: Number.parseFloat(l.unitPrice as unknown as string),
                }))
              : [{ position: 0, description: '', quantity: 1, unitPrice: 0 }],
          );
        }
      } catch (e) {
        onError(e);
      }
    })();
  }, [id, isNew, onError]);

  const base = useMemo(
    () => lines.reduce((a, l) => a + l.quantity * l.unitPrice, 0),
    [lines],
  );
  const irpf = base * (irpfPct / 100);
  const iva = base * (ivaPct / 100);
  const total = base - irpf + iva;

  async function save() {
    setBusy(true);
    try {
      if (isNew) {
        if (!clientId) {
          onToast('Selecciona un cliente', 'err');
          setBusy(false);
          return;
        }
        const r = await api<{ invoice: Invoice }>('/admin/invoices-issued', {
          method: 'POST',
          body: {
            clientId,
            concept,
            dueDate: dueDate || null,
            irpfPct,
            ivaPct,
            notes: notes || null,
            isPrivate,
            lines: lines.filter((l) => l.description),
          },
        });
        setInvoice(r.invoice);
        const cli = allClients.find((c) => c.id === clientId);
        if (cli) setClient(cli);
        onToast('Borrador guardado', 'ok');
      } else {
        const r = await api<{ invoice: Invoice }>(`/admin/invoices-issued/${id}`, {
          method: 'PATCH',
          body: editable
            ? {
                clientId,
                concept,
                dueDate: dueDate || null,
                irpfPct,
                ivaPct,
                notes: notes || null,
                isPrivate,
                lines: lines.filter((l) => l.description),
              }
            : { payDate: invoice?.payDate ?? null },
        });
        setInvoice(r.invoice);
        onToast('Cambios guardados', 'ok');
      }
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function emit() {
    if (!invoice) return;
    setBusy(true);
    try {
      const r = await api<{ invoice: Invoice }>(`/admin/invoices-issued/${invoice.id}/emit`, {
        method: 'POST',
      });
      setInvoice(r.invoice);
      onToast(`Factura emitida ${r.invoice.formattedNumber}`, 'ok');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!invoice) return;
    setBusy(true);
    try {
      const r = await api<{ invoice: Invoice }>(`/admin/invoices-issued/${invoice.id}/send`, {
        method: 'POST',
        body: { sendDate: todayIso() },
      });
      setInvoice(r.invoice);
      onToast('Marcada como enviada', 'ok');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function pay() {
    if (!invoice) return;
    setBusy(true);
    try {
      const r = await api<{ invoice: Invoice }>(`/admin/invoices-issued/${invoice.id}/pay`, {
        method: 'POST',
        body: { payDate: todayIso() },
      });
      setInvoice(r.invoice);
      onToast('Marcada como cobrada', 'ok');
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function togglePrivate(next: boolean) {
    setIsPrivate(next);
    // Before the draft is first saved there is no invoice yet — the flag rides
    // along with the create call. Afterwards, persist the toggle immediately so
    // it works in any status (no generic save button outside drafts).
    if (!invoice) return;
    try {
      await api(`/admin/invoices-issued/${invoice.id}`, {
        method: 'PATCH',
        body: { isPrivate: next },
      });
      setInvoice({ ...invoice, isPrivate: next });
      onToast(next ? 'Factura marcada como privada' : 'Factura visible para el equipo', 'ok');
    } catch (e) {
      setIsPrivate(!next);
      onError(e);
    }
  }

  async function openPdfPreview() {
    if (!invoice) return;
    try {
      const blob = await apiBlob(`/admin/invoices-issued/${invoice.id}/pdf`);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      onError(e);
    }
  }

  function closePdfPreview() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }

  function downloadPdf() {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `${invoice?.formattedNumber ?? 'factura'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <div className="body">
      <div className="pageh">
        <h1>{isNew ? 'Nueva factura' : invoice?.formattedNumber ?? '— borrador —'}</h1>
        {invoice && (
          <div className="sub">
            <InvoicePill status={invoice.status} />
          </div>
        )}
        <div className="actions">
          <button className="btn ghost" onClick={onClose}>← Volver</button>
          {editable && (
            <button className="btn" onClick={save} disabled={busy}>
              Guardar borrador
            </button>
          )}
          {invoice?.status === 'borrador' && (
            <button className="btn primary" onClick={emit} disabled={busy}>
              Emitir →
            </button>
          )}
          {invoice?.status === 'emitida' && (
            <button className="btn primary" onClick={send} disabled={busy}>
              Marcar enviada
            </button>
          )}
          {(invoice?.status === 'enviada' || invoice?.status === 'vencida') && (
            <button className="btn primary" onClick={pay} disabled={busy}>
              Cobrar
            </button>
          )}
          {invoice && invoice.status !== 'borrador' && (
            <button className="btn" onClick={openPdfPreview}>
              Ver PDF
            </button>
          )}
        </div>
      </div>

      <Timeline status={invoice?.status ?? 'borrador'} />

      <div className="editor" style={{ marginTop: 12 }}>
        <div className="invoice-paper">
          <div className="invh">
            <div className="l">
              <div className="num">Factura</div>
              <h2>{invoice?.formattedNumber ?? '— próximo número al emitir —'}</h2>
            </div>
            <div className="r">
              <div className="num">Emisión: {fmtDate(invoice?.issueDate)}</div>
              <div className="num">Vto.: {fmtDate(dueDate || invoice?.dueDate)}</div>
            </div>
          </div>

          <div className="parties">
            <div>
              <div className="ttl">Emisor</div>
              {issuer ? (
                <>
                  <div className="nm">{issuer.legalName}</div>
                  <div className="ln">NIF {issuer.nif}</div>
                  <div className="ln">{issuer.address}</div>
                  <div className="ln">
                    {[issuer.postalCode, issuer.city, issuer.country].filter(Boolean).join(' · ')}
                  </div>
                </>
              ) : (
                <div className="ln">—</div>
              )}
            </div>
            <div>
              <div className="ttl">Cliente</div>
              {editable && isNew ? (
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="field"
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--ink)', borderRadius: 4 }}
                >
                  <option value="">— selecciona —</option>
                  {allClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · {c.nif}
                    </option>
                  ))}
                </select>
              ) : (
                client && (
                  <>
                    <div className="nm">{client.name}</div>
                    <div className="ln">NIF {client.nif}</div>
                    <div className="ln">{client.address}</div>
                    <div className="ln">
                      {[client.postalCode, client.city, client.country].filter(Boolean).join(' · ')}
                    </div>
                  </>
                )
              )}
            </div>
          </div>

          <div className="lines">
            <table>
              <thead>
                <tr>
                  <th>Descripción</th>
                  <th className="num" style={{ width: 70 }}>Cant.</th>
                  <th className="num" style={{ width: 90 }}>Precio</th>
                  <th className="num" style={{ width: 100 }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx}>
                    <td>
                      <input
                        value={l.description}
                        readOnly={!editable}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        step="0.001"
                        value={l.quantity}
                        readOnly={!editable}
                        onChange={(e) => updateLine(idx, { quantity: Number.parseFloat(e.target.value) || 0 })}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        step="0.01"
                        value={l.unitPrice}
                        readOnly={!editable}
                        onChange={(e) => updateLine(idx, { unitPrice: Number.parseFloat(e.target.value) || 0 })}
                      />
                    </td>
                    <td className="num">{(l.quantity * l.unitPrice).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editable && (
              <div className="addrow">
                <button onClick={() => setLines((ls) => [...ls, { position: ls.length, description: '', quantity: 1, unitPrice: 0 }])}>
                  + añadir línea
                </button>
              </div>
            )}
          </div>

          <div className="totals">
            <div className="grid">
              <div className="lbl">Base imponible</div>
              <div className="v">{base.toFixed(2)} €</div>
              <div className="lbl">IRPF {irpfPct}%</div>
              <div className="v">-{irpf.toFixed(2)} €</div>
              <div className="lbl">IVA {ivaPct}%</div>
              <div className="v">{iva.toFixed(2)} €</div>
              <div className="lbl total">TOTAL</div>
              <div className="v total">{total.toFixed(2)} €</div>
            </div>
          </div>
        </div>

        <div className="sidebar-r">
          <div className="card">
            <div className="head"><h3>Datos</h3></div>
            <div className="pad" style={{ display: 'grid', gap: 10 }}>
              <div className="field">
                <label>Concepto</label>
                <input value={concept} readOnly={!editable} onChange={(e) => setConcept(e.target.value)} />
              </div>
              <div className="field">
                <label>Vencimiento</label>
                <DateInput value={dueDate} onChange={setDueDate} readOnly={!editable} />
              </div>
              <div className="field">
                <label>IRPF %</label>
                <input
                  type="number"
                  step="0.01"
                  value={irpfPct}
                  readOnly={!editable}
                  onChange={(e) => setIrpfPct(Number.parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="field">
                <label>IVA %</label>
                <input
                  type="number"
                  step="0.01"
                  value={ivaPct}
                  readOnly={!editable}
                  onChange={(e) => setIvaPct(Number.parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="field">
                <label>Notas</label>
                <textarea
                  rows={3}
                  value={notes}
                  readOnly={!editable}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              {isAdmin && (
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}
                >
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => void togglePrivate(e.target.checked)}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  <span>Privada — solo el admin la ve</span>
                </label>
              )}
            </div>
          </div>

          {invoice && invoice.status !== 'borrador' && (
            <div className="card">
              <div className="head"><h3>Resumen</h3></div>
              <div className="pad" style={{ display: 'grid', gap: 6, fontFamily: 'Geist Mono, monospace', fontSize: 11 }}>
                <div>Emisión: {fmtDate(invoice.issueDate)}</div>
                <div>Envío: {fmtDate(invoice.sendDate)}</div>
                <div>Cobro: {fmtDate(invoice.payDate)}</div>
                <div>Total: {fmt(invoice.totalAmount)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {pdfUrl && (
        <div className="modal-backdrop" onClick={closePdfPreview}>
          <div
            className="modal pdf"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3>{invoice?.formattedNumber ?? 'Factura'}</h3>
            <iframe src={pdfUrl} title="Vista previa de la factura" />
            <div className="actions">
              <button type="button" className="btn ghost" onClick={closePdfPreview}>
                Cerrar
              </button>
              <button type="button" className="btn primary" onClick={downloadPdf}>
                Descargar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function updateLine(idx: number, patch: Partial<Line>): void {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
}

function Timeline({ status }: { status: InvoiceStatus }) {
  const idx = STEPS.indexOf(status === 'vencida' ? 'enviada' : status);
  return (
    <div className="timeline">
      {STEPS.map((s, i) => (
        <div key={s} className={`step ${i < idx ? 'done' : i === idx ? 'curr' : ''}`}>
          <div className="dot" />
          <div className="lab">{s}</div>
        </div>
      ))}
    </div>
  );
}
